import { createWebHistory, createRouter } from 'vue-router'
import { HTTP } from '@/services/http'
import { UserService } from '@/services/user-service'
import { traverse } from '@/modules/util/object'

import appRoutes from './app'
import authRoutes from './auth'
import vehicleRoutes from './vehicles'
import inspectionRoutes from './inspections'

import NotFound from '@/views/NotFound.vue'
import { useInspectionStore } from '@/stores/inspection-store'
import { useVehicleStore } from '@/stores/vehicle-store'
import { Notify } from '@/modules/util/notify'

const routes = [
	...appRoutes,
	...authRoutes,
	...vehicleRoutes,
	...inspectionRoutes,
	{
		path: '/:pathMatch(.*)*', 
		name: 'not-found', 
		component: NotFound 
	},
]

const router = createRouter({
	history: createWebHistory(),
	routes,
})

HTTP.interceptors.request = (http, config) => {
	const key = config.method === 'GET' ? 'params' : 'body'
	const { username, usertoken } = UserService.getAuth()

	if (!config[key].username) {
		config[key].username = username
	}

	if (!config[key].usertoken) {
		config[key].usertoken = usertoken
	}

	const location_id = localStorage.getItem('LOCATION_ID')
	if (location_id) {
		config[key].location_id = parseInt(location_id)
	}

	// By only testing for username, we avoid blocking login requests.
	// We have the response hook as a fallback if the usertoken is missing.
	if (!config[key].username) {
		router.push({ name: 'login' })
		return false	// Cancel the request
	}

	return config
}

HTTP.interceptors.response = (http, res) => {
	if (res.body.errorType === 'AUTHENTICATION_FAILED') {
		router.push({ name: 'login' })
	}
    
	return res
}

HTTP.interceptors.cacheRequest = async (item) => {
	if (!item?.value?.request)
		return null

	if (item.value.path !== '/saveinspection')
		return item

	let ok = true
	item.uids = []

	const inject = async (key, value, parent) => {
		if (!value?.uid)
			return

		const dep = await HTTP.cache.get(value.uid)
		if (dep === null) {
			// The upload record is gone and won't return, so holding the save would block it forever.
			// Drop the dead local reference rather than storing it, and record the lost image.
			if (typeof value.image === 'string' && value.image.startsWith('blob:')) {
				console.error(`saveinspection: image upload ${value.uid} missing from cache; image lost`, key)
				delete value.image
			}
			return
		}

		if (!dep.value?.response) {
			ok = false
			return
		}

		value.imagename = dep.value.response.body.imagename
		item.uids.push(value.uid)
		delete value.uid

		if (value.image) {
			URL.revokeObjectURL(value.image)
			delete value.image
		}
	}

	let body, vehicle, inspection

	if (item.value.request.body) {
		try {
			body = JSON.parse(item.value.request.body)
			vehicle = body.vehicle ? JSON.parse(body.vehicle) : null
			inspection = body.inspection ? JSON.parse(body.inspection) : null
		} catch (e) {
			console.error('Failed to parse request body:', e)
			return null
		}
	}

	if (vehicle) {
		await traverse(vehicle, inject)
		body.vehicle = JSON.stringify(vehicle)
	}

	if (inspection) {
		await traverse(inspection, inject)
		body.inspection = JSON.stringify(inspection)
	}

	if (body)
		item.value.request.body = JSON.stringify(body)

	await HTTP.cache.set(item.value, item.uid)

	return ok ? item : null
}

HTTP.interceptors.cacheResponse = (http, res, item) => {
	if (!res.status)
		return
    
	if (res.body && res.body.errorType === 'AUTHENTICATION_FAILED') {
		HTTP.cache.remove(item.uid)
		Notify.info('Authentication failed. Please log in')
		router.push({ name: 'login' })
		return
	}

	if (item.value.path === '/uploadinspectionimage') {
		if (res.body?.imagename && item.value.request) {
			delete item.value.request
			item.value.response = res
            
			HTTP.cache.set(item.value, item.uid).catch(e => {
				console.error('Failed to cache image upload:', e)
				HTTP.cache.remove(item.uid)
			})
		}

		return
	}

	HTTP.cache.remove(item.uid)

	if (!res.body?.inspection || item.value.path !== '/saveinspection') {
		return
	}

	Notify.success(`${
		res.body.inspection?.motorvehicleinspection_data?.vehicle?.registration || 
        res.body.inspection?.motorvehicleinspection_data?.vehicle_id
	}: Inspection uploaded`)

	// Only delete cached dependencies once we're certain the inspection was uploaded.
	// This means we don't break the cacheRequest safeguard, which refuses to upload anything
	// with embedded dependencies still available in the cache.
	HTTP.cache.remove(item.uids)

	const vehicleStore = useVehicleStore()
	const vehicle = vehicleStore.currentVehicle

	if (
		!vehicle?.id ||
        !res.body.inspection?.motorvehicle_id ||
        String(vehicle.id) !== String(res.body.inspection.motorvehicle_id)
	) {
		return
	}

	const inspectionStore = useInspectionStore()
	const inspections = inspectionStore.allInspections || []
	const inspection = res.body.inspection

	inspection.display_datetime = inspection.motorvehicleinspection_datetime
		.split(' ')
		.map((v, i) => {
			if (i) return v
			return v.split('-').reverse().join('/')
		})
		.join(' ')

	inspections.unshift(inspection)
	inspectionStore.setInspections(inspections)

	const data = inspection.motorvehicleinspection_data?.vehicle || {}

	Object.keys(data).forEach(key => {
		vehicle[key] = data[key]
	})

	vehicleStore.setCurrentVehicle(vehicle)
}

export default router